import { Spinner, useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { debounce } from 'throttle-debounce';

import { MapContent } from './MapContent';
import { MAP_INTERACTION_RELOAD_DELAY } from './constants';
import { useReloadHybridSearchAfterMapInteraction } from './hooks/useReloadHybridSearchAfterMapInteraction';
import { useYMapsInitialization } from './hooks/useYMapsInitialization';
import * as styles from './styles.css';
import { useTrackMapInteraction } from './utils/useTrackMapInteraction';
import { ExpPinsAndPopupRedesign } from '../../../common/constants/config/declarations';
import { useApplicationContext } from '../../../common/containers/ApplicationContext';
import { useIsMultipinsExpActive } from '../../../common/hooks/useIsMultipinsExpActive';
import { selectIsResultsLoading, selectResultsStatus } from '../../../common/selectors/results';
import { IMapStatePayload, MapController } from '../../controller/MapController';
import { MapControllerProvider } from '../../controller/MapControllerContext';

export function Map() {
  const { config } = useApplicationContext();
  const isMobile = useDeviceType() !== 'desktop';
  const elementRef = React.useRef<HTMLDivElement>(null);
  const isResultsLoading = useSelector(selectIsResultsLoading);
  const resultsStatus = useSelector(selectResultsStatus);
  const trackMapInteraction = useTrackMapInteraction();
  const reloadHybridSearchAfterMapInteraction = useReloadHybridSearchAfterMapInteraction();
  const handleMapInteractions = React.useCallback(() => trackMapInteraction(), [trackMapInteraction]);

  // MapController захватывает onUserIntent один раз при инициализации, а reload зависит от
  // deviceType/dispatch и может менять идентичность — поэтому держим актуальную ссылку в ref,
  // а сам обработчик делаем стабильным.
  const reloadRef = React.useRef(reloadHybridSearchAfterMapInteraction);
  reloadRef.current = reloadHybridSearchAfterMapInteraction;

  // Обновление данных карты и листинга при любых пользовательских взаимодействиях с картой.
  // Не реагирует на программные взаимодействия (сайдбар, внешние кнопки) — это на откуп тех компонентов.
  // Дебаунсим: серия actionend при быстром зуме/перетаскивании схлопывается в один запрос на снимке
  // последнего жеста. Снимок (bounds + zoom) консистентен и свежий — в отличие от отстающего redux mapBounds.
  const handleUserIntent = React.useMemo(
    () =>
      debounce(MAP_INTERACTION_RELOAD_DELAY, (payload: IMapStatePayload) => {
        reloadRef.current({ mapSnapshot: { bounds: payload.visibleBounds, zoom: payload.zoom } });
      }),
    [],
  );

  React.useEffect(() => () => handleUserIntent.cancel(), [handleUserIntent]);

  // запуск экспа на мобилках с возможностью выключить. Десктоп можно принудительно включить без эксперимента
  const isPinsRedesign =
    (ExpPinsAndPopupRedesign.use() === 'B' &&
      config.get<boolean>('BX.PinsAndPopupRedesign.2026.Enabled') &&
      isMobile) ||
    (!isMobile && config.get<boolean>('BX.PinsAndPopupRedesign.2026.Desktop.Enabled')) ||
    false;
  // Монтируем ради exposure эксперимента мультипинов: ветвление данных живёт в fetchMapData
  // через Redux-селектор, а он exposure не отправляет
  useIsMultipinsExpActive();

  const { ymaps, map, mapController } = useYMapsInitialization({
    elementRef,
    onUserIntent: handleUserIntent,
  });

  const isMapReady = !!ymaps && !!map && !!mapController;

  return (
    <>
      <div
        ref={elementRef}
        className={styles['map']}
        onClick={handleMapInteractions}
        onDragStart={handleMapInteractions}
        onTouchStart={handleMapInteractions}
        /** На эти атрибуты смотрят webui тесты */
        data-clusters-status={resultsStatus}
        data-results-status={resultsStatus}
        data-name="Map"
        data-testid="Map"
      />
      {isMapReady && (
        <MapControllerProvider controller={mapController as MapController}>
          <MapContent ymaps={ymaps as YMaps.IYMaps} map={map as YMaps.Map} isRedesign={isPinsRedesign} />
        </MapControllerProvider>
      )}
      {isResultsLoading && (
        <div className={styles['spinner-container']}>
          <Spinner size={20} />
        </div>
      )}
    </>
  );
}
